import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { getCssVariableValue } from 'frontend-utils/utils/css-variable';
import type { MarketShape } from 'lumen/models/benchmark-insight/market-composition-market';
import getElement from 'lumen/modifiers/get-element';
import initGoogleMap from 'lumen/modifiers/init-google-map';

interface HotelBenchmarkInsightMarketCompositionMapMarketCompositionSignature {
  Args: {
    marketShape: MarketShape;
  };
}

export default class HotelBenchmarkInsightMarketCompositionMapMarketComposition extends Component<HotelBenchmarkInsightMarketCompositionMapMarketCompositionSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  ownHotelMarker: google.maps.Marker | null = null;
  ownHotelMarkerMouseOutListener: google.maps.MapsEventListener | null = null;
  ownHotelMarkerMouseOverListener: google.maps.MapsEventListener | null = null;
  ownHotelMarkerTooltip: google.maps.InfoWindow | null = null;
  ownHotelMarkerTooltipElement: Element | null = null;

  willDestroy(): void {
    super.willDestroy();

    this.ownHotelMarkerTooltip = null;
    this.ownHotelMarkerTooltipElement = null;
    this.ownHotelMarkerMouseOverListener?.remove();
    this.ownHotelMarkerMouseOutListener?.remove();
  }

  drawMarket(map: google.maps.Map): void {
    const marketShape = this.args.marketShape;

    const styleOptions = {
      fillColor: getCssVariableValue('--prism-color-general-info-300'),
      fillOpacity: 0.4,
      strokeWeight: 0,
      clickable: false,
    };

    marketShape.geometries.forEach((geometry) => {
      if (
        geometry.type === 'Point' &&
        marketShape.properties?.sub_type === 'Circle' &&
        geometry.coordinates[0] &&
        geometry.coordinates[1]
      ) {
        // For a circle, we need to draw it ourselves since GeoJSON doesn't support it natively
        const center = new google.maps.LatLng(
          geometry.coordinates[1],
          geometry.coordinates[0],
        );
        const radius = marketShape.properties.radius * 1000;

        if (center && typeof radius === 'number') {
          new google.maps.Circle({
            ...styleOptions,
            map,
            center,
            radius,
          });
        }
      }
    });

    const standardGeometries = marketShape.geometries.filter(
      (g) =>
        !(g.type === 'Point' && marketShape.properties?.sub_type === 'Circle'),
    );

    if (standardGeometries.length > 0) {
      map.data.addGeoJson({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'GeometryCollection',
              geometries: standardGeometries,
            },
          },
        ],
      });
      map.data.setStyle(styleOptions);
    }
  }

  drawOwnHotelMarker(map: google.maps.Map): void {
    if (this.hotelAndMarketService.hotel) {
      const { latitude, longitude } =
        this.hotelAndMarketService.hotel.hotelinfo;
      const ownHotelCoordinates = {
        lat: latitude,
        lng: longitude,
      };

      const ownHotelIcon = {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: getCssVariableValue('--prism-color-general-neutral-800'), // BENI-TODO: Add color variable
        fillOpacity: 1,
        strokeWeight: 0,
        scale: 3,
      };

      this.ownHotelMarker = new google.maps.Marker({
        map,
        position: ownHotelCoordinates,
        icon: ownHotelIcon,
      });
      this.ownHotelMarker.setCursor('default');

      if (!this.ownHotelMarkerTooltip) {
        this.ownHotelMarkerTooltip = new google.maps.InfoWindow({
          content: this.ownHotelMarkerTooltipElement,
          disableAutoPan: true, // Don't move the map if the infowindow is outside of the map.
        });
      }

      this.ownHotelMarkerMouseOverListener = this.ownHotelMarker.addListener(
        'mouseover',
        () => {
          this.ownHotelMarkerTooltip?.open({
            anchor: this.ownHotelMarker,
            map,
          });
        },
      );
      this.ownHotelMarkerMouseOutListener = this.ownHotelMarker.addListener(
        'mouseout',
        () => {
          this.ownHotelMarkerTooltip?.close();
        },
      );
    }
  }

  fitMapToMarketAndOwnHotelBounds(map: google.maps.Map): void {
    const bounds = new google.maps.LatLngBounds();

    const marketShape = this.args.marketShape;

    marketShape.geometries.forEach((geometry) => {
      // Polygon shape
      if (geometry.type === 'Polygon' && geometry.coordinates[0]) {
        geometry.coordinates[0].forEach((coordinate) => {
          if (coordinate[0] && coordinate[1]) {
            const latLng = new google.maps.LatLng(coordinate[1], coordinate[0]);
            bounds.extend(latLng);
          }
        });
      }
      // Circle shape
      else if (
        geometry.type === 'Point' &&
        marketShape.properties?.sub_type === 'Circle' &&
        geometry.coordinates[0] &&
        geometry.coordinates[1]
      ) {
        const center = new google.maps.LatLng(
          geometry.coordinates[1],
          geometry.coordinates[0],
        );
        const radius = marketShape.properties.radius * 1000;

        if (center && typeof radius === 'number') {
          // Create a temporary Google Maps Circle object to get its bounds
          const circle = new google.maps.Circle({ center, radius });
          const circleBounds = circle.getBounds();

          if (circleBounds) {
            bounds.union(circleBounds);
          }
        }
      }
    });

    // Include the hotel marker in the bounds, if it exists
    if (this.ownHotelMarker) {
      const ownHotelMarkerPosition = this.ownHotelMarker.getPosition();
      if (ownHotelMarkerPosition) {
        bounds.extend(ownHotelMarkerPosition);
      }
    }

    map.fitBounds(bounds, 1);
  }

  @action
  onInitGoogleMap(map: google.maps.Map): void {
    map.setOptions({
      zoomControl: false,
      draggable: false,
    });

    this.drawMarket(map);
    this.drawOwnHotelMarker(map);

    this.fitMapToMarketAndOwnHotelBounds(map);
  }

  @action
  setOwnHotelMarkerTooltipElement(element: Element): void {
    this.ownHotelMarkerTooltipElement = element;

    const ownHotelMarkerTooltipExists = this.ownHotelMarkerTooltip;
    if (ownHotelMarkerTooltipExists) {
      ownHotelMarkerTooltipExists.setContent(this.ownHotelMarkerTooltipElement);
    }
  }

  <template>
    <div
      class="market-composition__content__map"
      {{initGoogleMap this.onInitGoogleMap}}
    />

    <div hidden>
      <div
        class="prism-tooltip"
        {{getElement this.setOwnHotelMarkerTooltipElement}}
      >
        {{t "market_insight.map.legend.your_hotel"}}
      </div>
    </div>
  </template>
}
